import React from 'react';
import { Box, Typography, Paper, Grid, Divider, useTheme } from '@mui/material';
import { Compare as CompareIcon } from '@mui/icons-material';

const ComparisonBlock = ({ title, icon: CustomIcon, children, sx = {} }) => {
  const theme = useTheme();
  const IconComponent = CustomIcon || CompareIcon;
  
  return (
    <Paper
      elevation={theme.palette.mode === 'dark' ? 3 : 2}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        border: '1px solid',
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'divider',
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'background.paper',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          boxShadow: theme.palette.mode === 'dark' 
            ? '0 8px 24px rgba(0,0,0,0.5)' 
            : '0 8px 24px rgba(0,0,0,0.1)',
          transform: 'translateY(-2px)'
        },
        ...sx
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconComponent sx={{ 
          fontSize: '1.75rem', 
          mr: 1.5, 
          color: theme.palette.primary.main,
          filter: theme.palette.mode === 'dark' ? 'drop-shadow(0 0 2px rgba(64, 195, 255, 0.5))' : 'none'
        }} />
        <Typography variant="h6" component="div" fontWeight="bold" sx={{
          color: theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main
        }}>
          {title}
        </Typography>
      </Box>
      <Divider sx={{ 
        mb: 2,
        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'divider'
      }} />
      <Box>
        {children}
      </Box>
    </Paper>
  );
};

// Usage with two columns
export const TwoColumnComparison = ({ title, leftTitle, rightTitle, leftContent, rightContent, icon }) => {
  const theme = useTheme();
  
  return (
    <ComparisonBlock title={title} icon={icon}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ 
            p: 2.5, 
            height: '100%', 
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'background.paper', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : theme.palette.divider,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : theme.palette.action.hover
            }
          }}>
            <Typography 
              variant="subtitle1" 
              fontWeight="bold" 
              color={theme.palette.mode === 'dark' ? theme.palette.primary.light : theme.palette.primary.main} 
              gutterBottom
            >
              {leftTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
              {leftContent}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Box sx={{ 
            p: 2.5, 
            height: '100%', 
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'background.paper', 
            borderRadius: 2,
            border: '1px solid',
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : theme.palette.divider,
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : theme.palette.action.hover
            }
          }}>
            <Typography 
              variant="subtitle1" 
              fontWeight="bold" 
              color={theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.main} 
              gutterBottom
            >
              {rightTitle}
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
              {rightContent}
            </Typography>
          </Box>
        </Grid>
      </Grid>
    </ComparisonBlock>
  );
};

export default ComparisonBlock;